import { FormEvent, useState } from "react";
import { FilterActions } from "./FilterActions.tsx";
import { isEmptyRange, NumberRange, orderedRange } from "../numberRange.ts";

function rangeBoundToInput(bound: number | undefined): string {
  return bound === undefined ? "" : String(bound);
}

function inputToRangeBound(
  input: string,
  allowDecimals: boolean,
): number | undefined {
  const number = Number(input.replace(",", "."));
  if (!input.trim() || !Number.isFinite(number)) return undefined;
  return allowDecimals ? number : Math.round(number);
}

const inputClassName =
  "w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-700 focus:outline-none focus:border-slate-500";

export function NumberRangeFilter({
  value,
  onApply,
  close,
  allowDecimals,
}: {
  value: NumberRange;
  onApply: (range: NumberRange) => void;
  close: () => void;
  allowDecimals: boolean;
}) {
  const [min, setMin] = useState(rangeBoundToInput(value.min));
  const [max, setMax] = useState(rangeBoundToInput(value.max));

  function apply(range: NumberRange) {
    onApply(range);
    close();
  }

  function applyTypedRange(event: FormEvent) {
    event.preventDefault();
    apply(
      orderedRange({
        min: inputToRangeBound(min, allowDecimals),
        max: inputToRangeBound(max, allowDecimals),
      }),
    );
  }

  return (
    <form onSubmit={applyTypedRange} className="flex flex-col gap-2 px-2">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          Min
          <input
            inputMode={allowDecimals ? "decimal" : "numeric"}
            value={min}
            onChange={(event) => setMin(event.target.value)}
            placeholder="Any"
            className={inputClassName}
            autoFocus
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          Max
          <input
            inputMode={allowDecimals ? "decimal" : "numeric"}
            value={max}
            onChange={(event) => setMax(event.target.value)}
            placeholder="Any"
            className={inputClassName}
          />
        </label>
      </div>
      <p className="text-xs text-slate-400">
        Fill only one field to keep values above or below it.
      </p>
      <FilterActions
        canClear={!isEmptyRange(value)}
        onClear={() => apply({})}
      />
    </form>
  );
}
